import React, { useState, useMemo } from 'react';
import useSWR from 'swr';
import { 
  TrendingUp,
  TrendingDown,
  Calendar,
  DollarSign,
  Users,
  Clock,
  AlertCircle,
  Plus,
  ChevronRight,
  BarChart3,
  CheckCircle2,
  Download,
  FileJson,
  FileSpreadsheet,
  Wallet,
  RotateCcw,
  X
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import api from '../utils/api';
import { toast } from 'sonner';

// SWR fetcher
const fetcher = (url) => api.get(url).then(res => res.data);

// Chart colors
const COLORS = ['#3b82f6', '#94a3b8'];

// Helper function outside component
const formatTime = (time) => {
  if (!time) return '';
  try {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  } catch {
    return time;
  }
};

// TrendIndicator component - only shows when there's valid comparison data
const TrendIndicator = ({ value, previous, suffix = '%' }) => {
  // Don't show trend if:
  // - No value or value is 0
  // - No previous data to compare against
  // - The trend calculation would be meaningless (dividing by 0)
  if (value === null || value === undefined || !previous || previous === 0) {
    return null;
  }
  
  if (value === 0) return null;
  
  const isPositive = value > 0;
  return (
    <span className={`flex items-center gap-0.5 text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
      {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {Math.abs(value).toFixed(0)}{suffix}
    </span>
  );
};

// Custom tooltip for bar chart - shows completed and projected separately
const CustomTooltip = ({ active, payload, label, currencySymbol = '$' }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload || {};
    const completed = data.completed || 0;
    const projected = data.projected || 0;
    const total = completed + projected;
    const completedBookings = data.completedBookings || data.bookings || 0;
    const projectedBookings = data.projectedBookings || 0;
    const isCurrentMonth = data.isCurrentMonth;
    
    return (
      <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3 text-sm min-w-[140px]">
        <p className="font-medium text-gray-900 mb-2">{label}</p>
        {completed > 0 && (
          <div className="flex items-center justify-between gap-3 mb-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-green-600"></span>
              <span className="text-gray-600 text-xs">Completed</span>
            </span>
            <span className="text-green-600 font-semibold">{currencySymbol}{completed.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
          </div>
        )}
        {projected > 0 && (
          <div className="flex items-center justify-between gap-3 mb-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-green-300"></span>
              <span className="text-gray-600 text-xs">Projected</span>
            </span>
            <span className="text-green-500 font-semibold">{currencySymbol}{projected.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
          </div>
        )}
        {(completed > 0 || projected > 0) && (
          <div className="pt-1 mt-1 border-t border-gray-100 flex items-center justify-between">
            <span className="text-gray-500 text-xs">Total</span>
            <span className="text-gray-900 font-bold">{currencySymbol}{total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
          </div>
        )}
        {total === 0 && (
          <p className="text-gray-400 text-xs">No revenue</p>
        )}
      </div>
    );
  }
  return null;
};

// Custom shape for completed bar - rounded top only when no projected data stacked on top
const CompletedBarShape = (props) => {
  const { x, y, width, height, payload } = props;
  const hasProjected = (payload?.projected || 0) > 0;
  const radius = 4;
  
  if (!height || height <= 0) return null;
  
  // If there's projected data stacked on top, no rounded corners on completed
  if (hasProjected) {
    return <rect x={x} y={y} width={width} height={height} fill="#22c55e" />;
  }
  
  // No projected data - add rounded top corners
  return (
    <path
      d={`
        M ${x},${y + height}
        L ${x},${y + radius}
        Q ${x},${y} ${x + radius},${y}
        L ${x + width - radius},${y}
        Q ${x + width},${y} ${x + width},${y + radius}
        L ${x + width},${y + height}
        Z
      `}
      fill="#22c55e"
    />
  );
};

// Custom X-axis tick to show "today/this week/this month" indicator
// Responsive: smaller text on mobile, first letter only for many months
const CustomXAxisTick = ({ x, y, payload, granularity, isMobile, dataLength, startDate, endDate }) => {
  let isCurrent = false;
  const today = new Date();
  
  // Check if today falls within the selected date range
  const rangeStart = startDate ? new Date(startDate) : null;
  const rangeEnd = endDate ? new Date(endDate) : null;
  const todayInRange = rangeStart && rangeEnd && today >= rangeStart && today <= rangeEnd;
  
  if (todayInRange) {
    if (granularity === 'daily') {
      // Check if viewing current day
      const todayDayName = today.toLocaleDateString('en-US', { weekday: 'short' });
      const todayDate = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      isCurrent = payload?.value === todayDayName || payload?.value === todayDate;
    } else if (granularity === 'weekly') {
      // Don't highlight weeks - too complex to determine "current week"
      isCurrent = false;
    } else if (granularity === 'monthly') {
      // Only highlight if current month label matches
      const currentMonthLabel = today.toLocaleDateString('en-US', { month: 'short' });
      isCurrent = payload?.value === currentMonthLabel;
    } else if (granularity === 'yearly') {
      const currentYear = today.getFullYear().toString();
      isCurrent = payload?.value === currentYear;
    }
  }
  
  // Smaller font on mobile, use first letter for months if 12+ data points on mobile
  const fontSize = isMobile ? 9 : 11;
  const dotSize = isMobile ? 2 : 3;
  const dotY = isMobile ? 18 : 22;
  
  // Use first letter only for monthly view with 12+ months on mobile
  let displayValue = payload?.value;
  if (isMobile && granularity === 'monthly' && dataLength >= 12) {
    // Get first letter: Jan -> J, Feb -> F, etc.
    displayValue = payload?.value?.charAt(0) || payload?.value;
  }
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text 
        x={0} 
        y={0} 
        dy={10} 
        textAnchor="middle" 
        fill={isCurrent ? '#2563eb' : '#6b7280'}
        fontSize={fontSize}
        fontWeight={isCurrent ? 600 : 400}
      >
        {displayValue}
      </text>
      {isCurrent && (
        <circle cx={0} cy={dotY} r={dotSize} fill="#2563eb" />
      )}
    </g>
  );
};

const OperationsDashboard = ({ onNavigate }) => {
  // Default to current calendar year (Jan 1 - Dec 31)
  const getDefaultDates = () => {
    const today = new Date();
    const year = today.getFullYear();
    
    return {
      start: `${year}-01-01`,
      end: `${year}-12-31`
    };
  };
  
  // Memoize default dates so they don't change on every render
  const defaultDates = useMemo(() => getDefaultDates(), []);
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  
  // Check if dates are at default (for showing reset button)
  const isDefaultDateRange = startDate === defaultDates.start && endDate === defaultDates.end;
  
  // Reset to default date range
  const resetDateRange = () => {
    const defaults = getDefaultDates();
    setStartDate(defaults.start);
    setEndDate(defaults.end);
  };
  
  // Date limits: 5 years ago to 6 months forward
  const getDateLimits = () => {
    const today = new Date();
    const fiveYearsAgo = new Date(today);
    fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
    const sixMonthsForward = new Date(today);
    sixMonthsForward.setMonth(sixMonthsForward.getMonth() + 6);
    
    return {
      minDate: fiveYearsAgo.toISOString().split('T')[0],
      maxDate: sixMonthsForward.toISOString().split('T')[0]
    };
  };
  
  const dateLimits = getDateLimits();

  // Build the API URL with custom dates
  const getApiUrl = () => {
    return `/dashboard/stats?period=custom&startDate=${startDate}&endDate=${endDate}`;
  };

  // useSWR for data fetching - auto revalidates on focus and every 30 seconds
  const { data: stats, error, isLoading, isValidating } = useSWR(
    startDate && endDate ? getApiUrl() : null,
    fetcher,
    {
      revalidateOnFocus: true,
      refreshInterval: 30000, // Refresh every 30 seconds for real-time data
      dedupingInterval: 5000, // Dedupe requests within 5 seconds
      keepPreviousData: true, // Keep showing old data while fetching new (prevents flash)
    }
  );
  
  // Show loading only on initial load (no data yet), not on revalidation
  const showLoading = isLoading && !stats;

  // Format currency using org's currency setting with 2 decimal places
  const formatCurrency = (amount, showPlus = false) => {
    const currencyCode = stats?.currency || 'USD';
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
    return showPlus && amount > 0 ? `+${formatted}` : formatted;
  };

  // Get currency symbol for inline display
  const getCurrencySymbol = () => stats?.currencySymbol || '$';

  const formatDateRange = () => {
    if (!stats?.dateRange?.start) return null;
    
    const formatDate = (dateStr) => {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
    
    const start = formatDate(stats.dateRange.start);
    const end = formatDate(stats.dateRange.end);
    
    if (start === end) {
      return start;
    }
    return `${start} - ${end}`;
  };

  const handleExport = async (format) => {
    try {
      if (format === 'csv') {
        // For CSV, we need to trigger a download
        const response = await api.get(`/dashboard/export?format=csv&period=custom&startDate=${startDate}&endDate=${endDate}`, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bookings_${startDate}_to_${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('CSV exported successfully!');
      } else {
        // JSON export
        const response = await api.get(`/dashboard/export?format=json&period=custom&startDate=${startDate}&endDate=${endDate}`);
        
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dashboard_${startDate}_to_${endDate}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('JSON exported successfully!');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export data');
    }
  };

  // Export dropdown for actions
  const exportActions = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <Download className="w-4 h-4" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport('json')} className="cursor-pointer">
          <FileJson className="w-4 h-4 mr-2" />
          Export as JSON
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Export as CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <TabPageLayout
      title="Dashboard"
      subtitle="Your operations at a glance"
      loading={showLoading}
      actions={
        <div className="hidden sm:block">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('json')} className="cursor-pointer">
                <FileJson className="w-4 h-4 mr-2" />
                Export as JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
    >
      <div className="space-y-4 sm:space-y-6">
        {/* Date Range Selector - Clean date pickers with optional reset */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2 flex-1 sm:flex-none">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={dateLimits.minDate}
              max={endDate}
              className="flex-1 sm:flex-none sm:w-[140px] px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              max={dateLimits.maxDate}
              className="flex-1 sm:flex-none sm:w-[140px] px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            />
            {/* Reset button - only show when dates are modified */}
            {!isDefaultDateRange && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={resetDateRange}
                className="text-gray-500 hover:text-gray-700 px-2"
                title="Reset to default date range"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {/* Mobile Export Button */}
          <div className="sm:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="border-gray-200 w-full justify-center">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('json')} className="cursor-pointer">
                  <FileJson className="w-4 h-4 mr-2" />
                  JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Stats Cards - 4 cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Total Revenue Card - Green */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100 rounded-xl p-3 sm:p-5">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center bg-green-100">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
              <TrendIndicator value={stats?.revenue?.trend} previous={stats?.revenue?.previous} />
            </div>
            <p className="text-xs text-green-700 uppercase tracking-wide font-medium">Net Revenue</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-800">
              {formatCurrency((stats?.revenue?.total || 0) + (stats?.projected?.total || 0))}
            </p>
            <div className="text-xs text-gray-500 mt-1 hidden sm:block space-y-0.5">
              {(stats?.projected?.total || 0) > 0 && (
                <p>{formatCurrency(stats?.revenue?.total || 0)} completed • {formatCurrency(stats?.projected?.total || 0)} projected</p>
              )}
              {(stats?.refunds?.total || 0) > 0 && (
                <p className="text-red-500">
                  {formatCurrency(stats?.refunds?.total || 0)} refunded ({stats?.refunds?.count || 0} bookings)
                </p>
              )}
            </div>
          </div>

          {/* Bookings Card - Blue */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-3 sm:p-5">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center bg-blue-100">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <TrendIndicator value={stats?.bookings?.trend} previous={stats?.bookings?.previous} />
            </div>
            <p className="text-xs text-blue-700 uppercase tracking-wide font-medium">Bookings</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-800">
              {stats?.bookings?.total || 0}
            </p>
            {(stats?.bookings?.delivered > 0 || stats?.bookings?.scheduled > 0) && (
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">
                {stats?.bookings?.delivered || 0} completed • {stats?.bookings?.scheduled || 0} upcoming
              </p>
            )}
          </div>

          {/* Avg Booking Value - Purple */}
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-100 rounded-xl p-3 sm:p-5">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center bg-purple-100">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-purple-700 uppercase tracking-wide font-medium">Avg Value</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-purple-800">
              {formatCurrency(stats?.avgBookingValue || 0)}
            </p>
          </div>

          {/* Leads Card - Orange (abandoned bookings = potential customers) */}
          <div 
            className={`bg-gradient-to-br rounded-xl p-3 sm:p-5 cursor-pointer transition-all hover:shadow-sm ${
              stats?.leads?.count > 0 
                ? 'from-orange-50 to-amber-50 border border-orange-200' 
                : 'from-gray-50 to-slate-50 border border-gray-200'
            }`}
            onClick={() => onNavigate('relations', { filter: 'lead' })}
          >
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${
                stats?.leads?.count > 0 ? 'bg-orange-100' : 'bg-gray-100'
              }`}>
                <Users className={`w-4 h-4 sm:w-5 sm:h-5 ${
                  stats?.leads?.count > 0 ? 'text-orange-600' : 'text-gray-400'
                }`} />
              </div>
              {stats?.leads?.count > 0 && (
                <span className="px-1.5 py-0.5 bg-orange-200 text-orange-800 text-xs font-semibold rounded-full hidden sm:inline">
                  Follow up
                </span>
              )}
            </div>
            <p className={`text-xs uppercase tracking-wide font-medium ${
              stats?.leads?.count > 0 ? 'text-orange-700' : 'text-gray-500'
            }`}>Leads</p>
            <p className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
              stats?.leads?.count > 0 ? 'text-orange-800' : 'text-gray-600'
            }`}>
              {stats?.leads?.count || 0}
            </p>
            {stats?.leads?.amount > 0 && (
              <p className="text-xs text-orange-600 mt-1 hidden sm:block">
                {formatCurrency(stats.leads.amount)} potential
              </p>
            )}
            {/* View Leads button - hidden on mobile to keep cards same height */}
            {stats?.leads?.count > 0 && (
              <button 
                onClick={() => onNavigate('relations', { filter: 'lead' })}
                className="mt-2 text-xs font-medium text-orange-600 hover:text-orange-700 hidden sm:flex items-center gap-1"
              >
                View Leads <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
        {/* Revenue Trend Chart - Full Width */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-gray-900">Revenue</h3>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 text-xs">
              <div className="flex items-center gap-1 sm:gap-1.5">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded" style={{ backgroundColor: '#22c55e' }}></div>
                <span className="text-gray-600">Completed</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-1.5">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded" style={{ backgroundColor: '#86efac', opacity: 0.6 }}></div>
                <span className="text-gray-600">Projected</span>
              </div>
            </div>
          </div>
          <div className="h-48 sm:h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={stats?.charts?.revenueTrend || []} 
                margin={{ top: 5, right: 5, left: -10, bottom: 5 }}
              >
                <XAxis 
                  dataKey="label" 
                  axisLine={false} 
                  tickLine={false} 
                  interval={0}
                  tick={(props) => (
                    <CustomXAxisTick 
                      {...props} 
                      granularity={stats?.charts?.granularity || 'monthly'} 
                      isMobile={window.innerWidth < 640}
                      dataLength={stats?.charts?.revenueTrend?.length || 0}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  )}
                  height={28}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  tickFormatter={(value) => `${getCurrencySymbol()}${value >= 1000 ? Math.round(value/1000) + 'k' : value}`}
                  width={45}
                />
                <Tooltip content={<CustomTooltip currencySymbol={getCurrencySymbol()} />} />
                {/* Stacked bars: Completed on bottom, Projected on top */}
                <Bar 
                  dataKey="completed" 
                  stackId="revenue"
                  shape={<CompletedBarShape />}
                  maxBarSize={40}
                  name="Completed"
                />
                <Bar 
                  dataKey="projected" 
                  stackId="revenue"
                  fill="#86efac"
                  fillOpacity={0.6}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                  name="Projected"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Today's Schedule */}
          <div className="bg-white rounded-xl overflow-hidden flex flex-col shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Today&apos;s Schedule</h3>
              </div>
              <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
                {stats?.today?.count || 0} bookings
              </span>
            </div>
            <div className="p-4 flex-1">
              {stats?.today?.bookings?.length > 0 ? (
                <div className="space-y-3">
                  {stats.today.bookings.slice(0, 5).map((booking, i) => (
                    <div 
                      key={booking.confirmationCode || i}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="text-sm font-semibold text-blue-600 whitespace-nowrap bg-blue-50 px-2 py-1 rounded">
                          {formatTime(booking.preferredTime)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">
                            {booking.customerName || 'Guest'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {booking.serviceName} {booking.partySize > 1 ? `• ${booking.partySize} guests` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        {booking.checkInStatus === 'checked_in' ? (
                          <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                            <CheckCircle2 className="w-4 h-4" />
                          </span>
                        ) : booking.paymentStatus === 'paid' ? (
                          <span className="w-2.5 h-2.5 bg-green-500 rounded-full block"></span>
                        ) : (
                          <span className="w-2.5 h-2.5 bg-orange-400 rounded-full block"></span>
                        )}
                      </div>
                    </div>
                  ))}
                  {stats.today.bookings.length > 5 && (
                    <p className="text-xs text-gray-500 text-center pt-2">
                      +{stats.today.bookings.length - 5} more bookings
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm font-medium">No bookings today</p>
                  <p className="text-xs text-gray-400 mt-1">Bookings will appear here</p>
                </div>
              )}
            </div>
            <div className="p-3 border-t border-gray-100">
              <button
                onClick={() => onNavigate('schedule')}
                className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1 py-1"
              >
                View Calendar <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Right Column - Insights */}
          <div className="space-y-4">
            {/* Tomorrow Preview */}
            {stats?.tomorrow?.count > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-100">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-blue-900 text-lg">Tomorrow</p>
                    <p className="text-sm text-blue-700">{stats.tomorrow.count} booking{stats.tomorrow.count > 1 ? 's' : ''} scheduled</p>
                  </div>
                </div>
              </div>
            )}

            {/* Booking Sources - Pie Chart */}
            {(stats?.bookings?.total > 0 || stats?.projected?.count > 0) && (
              <div className="bg-white rounded-xl p-4 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
                <h4 className="font-semibold text-gray-900 text-sm mb-4">Booking Sources</h4>
                <div className="flex items-center gap-6">
                  <div className="w-28 h-28">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Widget', value: stats.bookings.sources?.widget || 0 },
                            { name: 'Admin', value: stats.bookings.sources?.admin || 0 }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={25}
                          outerRadius={50}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {[0, 1].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <span className="text-sm text-gray-700">Widget (Online)</span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">{stats.bookings.sources?.widget || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                        <span className="text-sm text-gray-700">Admin (Manual)</span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">{stats.bookings.sources?.admin || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top Services by Revenue */}
            {stats?.topServices?.length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Top Services</h4>
                <div className="space-y-2">
                  {stats.topServices.slice(0, 4).map((service, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{service.name}</p>
                        <p className="text-xs text-gray-500">{service.count} booking{service.count !== 1 ? 's' : ''}</p>
                      </div>
                      <span className="text-sm font-semibold text-green-600 ml-3">
                        {formatCurrency(service.revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-xl p-4 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
              <h4 className="font-semibold text-gray-900 text-sm mb-3">Quick Actions</h4>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 h-auto py-3 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700"
                  onClick={() => onNavigate('schedule')}
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-xs sm:text-sm font-medium">Add Booking</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 h-auto py-3 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700"
                  onClick={() => onNavigate('schedule')}
                >
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs sm:text-sm font-medium">Calendar</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 h-auto py-3 hover:bg-green-50 hover:border-green-200 hover:text-green-700"
                  onClick={() => onNavigate('payouts')}
                >
                  <Wallet className="w-4 h-4" />
                  <span className="text-xs sm:text-sm font-medium">Payouts</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 h-auto py-3 hover:bg-purple-50 hover:border-purple-200 hover:text-purple-700"
                  onClick={() => onNavigate('relations')}
                >
                  <Users className="w-4 h-4" />
                  <span className="text-xs sm:text-sm font-medium">Customers</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity - Compact */}
        {stats?.recentActivity?.length > 0 && (
          <div className="bg-white rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 text-sm">Recent Activity</h3>
            </div>
            <div className="divide-y divide-gray-50">
              {stats.recentActivity.slice(0, 5).map((booking, i) => (
                <div key={booking.confirmationCode || i} className="px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      booking.paymentStatus === 'paid' ? 'bg-green-100' : 
                      booking.paymentStatus === 'refunded' ? 'bg-red-100' :
                      booking.paymentStatus === 'partially_refunded' ? 'bg-orange-100' :
                      'bg-amber-100'
                    }`}>
                      {booking.paymentStatus === 'paid' ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                      ) : booking.paymentStatus === 'refunded' ? (
                        <X className="w-3.5 h-3.5 text-red-600" />
                      ) : booking.paymentStatus === 'partially_refunded' ? (
                        <AlertCircle className="w-3.5 h-3.5 text-orange-600" />
                      ) : (
                        <Clock className="w-3.5 h-3.5 text-amber-600" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {booking.customerName || 'Guest'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{booking.serviceName}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    {booking.paymentStatus === 'partially_refunded' ? (
                      <>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatCurrency(booking.totalAmount || 0)}
                          <span className="text-red-500 ml-1">(-{formatCurrency(booking.refundedAmount || 0)})</span>
                        </p>
                        <p className="text-[10px] font-medium uppercase tracking-wide text-orange-500">
                          Partial Refund
                        </p>
                      </>
                    ) : booking.paymentStatus === 'refunded' ? (
                      <>
                        <p className="text-sm font-semibold text-red-600 line-through">
                          {formatCurrency(booking.totalAmount || 0)}
                        </p>
                        <p className="text-[10px] font-medium uppercase tracking-wide text-red-500">
                          Refunded
                        </p>
                      </>
                    ) : (
                      <>
                        <p className={`text-sm font-semibold ${
                          booking.paymentStatus === 'paid' ? 'text-green-600' : 'text-gray-700'
                        }`}>
                          {booking.paymentStatus === 'paid' ? '+' : ''}{formatCurrency(booking.totalAmount || 0)}
                        </p>
                        <p className={`text-[10px] font-medium uppercase tracking-wide ${
                          booking.paymentStatus === 'paid' ? 'text-green-600' : 'text-amber-500'
                        }`}>
                          {booking.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => onNavigate('schedule')}
                className="w-full text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1"
              >
                View All Bookings <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </TabPageLayout>
  );
};

export default OperationsDashboard;
